import React from 'react';
import { Terminal, Sparkles, Dog, Candy, Compass, ArrowRight, ShieldCheck } from 'lucide-react';
import { portfolioData } from '../data/portfolioData';

export default function Hero({ onOpenContact }) {
  const { profile } = portfolioData;

  return (
    <section id="about" className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
      
      {/* Background Parallax Mountain Contour & Subtle Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 1440 600">
          <path fill="none" stroke="currentColor" strokeWidth="1" className="text-brand-navy" d="M0,224L120,202.7C240,181,480,139,720,165.3C960,192,1200,288,1320,336L1440,384L1440,600L1320,600C1200,600,960,600,720,600C480,600,240,600,120,600L0,600Z"></path>
          <path fill="none" stroke="currentColor" strokeWidth="1.5" className="text-brand-bronze/40" d="M0,320L180,240C360,160,720,96,1080,160L1440,224"></path>
        </svg>
        <div className="absolute inset-0 bg-[radial-gradient(#1E3A5F_1px,transparent_1px)] [background-size:24px_24px] opacity-40"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Headline & Intro */}
          <div className="lg:col-span-7 space-y-6 text-left">
            
            {/* Status Pill */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 dark:bg-slate-900/90 border border-slate-300 dark:border-slate-700/80 backdrop-blur-md shadow-inner text-xs text-slate-700 dark:text-slate-300 font-mono">
              <span className="w-2 h-2 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-ping"></span>
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold">ONLINE</span>
              <span className="text-slate-400 dark:text-slate-600">|</span>
              <span className="text-slate-700 dark:text-slate-300">{profile.statusPill.text}</span>
              <span className="hidden sm:inline text-brand-bronze font-semibold">({profile.statusPill.subtext})</span>
            </div>

            {/* Title & Tagline */}
            <div className="space-y-3">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-tight">
                Hey, I'm <span className="text-gold-gradient">{profile.name}</span>.
              </h1>
              <p className="text-xl sm:text-2xl font-medium text-slate-700 dark:text-slate-300">
                Tech Builder • Tidy Home Enthusiast • Creative Engineer
              </p>
            </div>

            {/* Amazon Persona Card Highlight */}
            <div className="p-4 rounded-2xl bg-white/90 dark:bg-gradient-to-r dark:from-slate-900/90 dark:via-slate-900/60 dark:to-slate-950 border border-slate-200 dark:border-slate-800 backdrop-blur-md relative overflow-hidden group shadow-md dark:shadow-none">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-bronze/10 rounded-full blur-2xl group-hover:bg-brand-bronze/20 transition-all"></div>
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-brand-bronze/15 border border-brand-bronze/30 text-brand-bronze shrink-0">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="space-y-1">
                  <div className="text-xs font-mono uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                    <span>Shopping Habits Persona Summary</span>
                    <ShieldCheck className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                  </div>
                  <p className="text-sm italic text-slate-800 dark:text-slate-200 leading-relaxed">
                    "{profile.amazonPersonaQuote}"
                  </p>
                </div>
              </div>
            </div>

            {/* Bio Description */}
            <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed max-w-2xl">
              {profile.about}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="#persona"
                className="px-6 py-3.5 rounded-xl bg-gradient-to-r from-brand-bronze to-amber-600 hover:from-brand-bronze-hover hover:to-amber-500 text-slate-950 font-bold text-sm shadow-lg shadow-amber-950/30 flex items-center gap-2 transition-all transform hover:-translate-y-0.5"
              >
                <Dog className="w-4 h-4" /> Co-Pilot & Persona Hub <ArrowRight className="w-4 h-4" />
              </a>

              <a
                href="#tech"
                className="px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-200 font-semibold text-sm border border-slate-800 hover:border-slate-700 flex items-center gap-2 transition-all"
              >
                <Terminal className="w-4 h-4 text-brand-bronze" /> Tech Stack
              </a>

              <button
                onClick={onOpenContact}
                className="px-6 py-3.5 rounded-xl bg-brand-navy/30 hover:bg-brand-navy/60 text-blue-300 font-semibold text-sm border border-blue-500/30 hover:border-blue-400/50 flex items-center gap-2 transition-all"
              >
                Get In Touch
              </button>
            </div>

            {/* Key Persona Tags */}
            <div className="flex flex-wrap items-center gap-3 pt-4 text-xs font-mono text-slate-400 border-t border-slate-800/80">
              <span className="flex items-center gap-1 bg-slate-900/80 px-2.5 py-1 rounded-lg border border-slate-800">
                💻 Tech-Savvy
              </span>
              <span className="flex items-center gap-1 bg-slate-900/80 px-2.5 py-1 rounded-lg border border-slate-800">
                🐕 Dog Loving
              </span>
              <span className="flex items-center gap-1 bg-slate-900/80 px-2.5 py-1 rounded-lg border border-slate-800">
                🍬 Sour Candy Obsessed
              </span>
              <span className="flex items-center gap-1 bg-slate-900/80 px-2.5 py-1 rounded-lg border border-slate-800">
                🏠 Tidy Home Setup
              </span>
            </div>

          </div>

          {/* Right Column: Interactive Logo Card Showcase */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative w-full max-w-md">
              
              {/* Glow Aura */}
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-brand-navy via-brand-bronze to-slate-800 opacity-40 blur-xl animate-glow"></div>
              
              {/* Outer Card */}
              <div className="relative rounded-3xl bg-slate-900/90 border border-slate-800 p-6 backdrop-blur-xl shadow-2xl space-y-6">
                
                {/* Header Bar */}
                <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-emerald-500/80"></div>
                  </div>
                  <span className="text-xs font-mono text-slate-400">djsynthe.com</span>
                </div>

                {/* Logo Frame */}
                <div className="relative group p-6 rounded-2xl bg-gradient-to-b from-slate-950 to-slate-900 border border-slate-800/80 flex flex-col items-center justify-center text-center overflow-hidden">
                  <img 
                    src="/logo.png" 
                    alt="Synthe JN Logo" 
                    className="w-48 h-auto object-contain transition-transform duration-500 group-hover:scale-105 filter drop-shadow-[0_10px_20px_rgba(30,58,95,0.4)]"
                  />
                  <div className="mt-4 pt-4 border-t border-slate-800/80 w-full flex items-center justify-between text-xs text-slate-400 font-mono">
                    <span>BRAND IDENTITY</span>
                    <span className="text-brand-bronze">JN MOUNTAIN</span>
                  </div>
                </div>

                {/* Quick Interactive Terminal Snippet */}
                <div className="p-3.5 rounded-xl bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono text-xs text-slate-800 dark:text-slate-300 space-y-1.5 shadow-sm dark:shadow-none">
                  <div className="text-slate-500 dark:text-slate-500">$ synthe --status</div>
                  <div className="text-emerald-600 dark:text-emerald-400">✔ Setup: Clean & Tidy</div>
                  <div className="text-amber-600 dark:text-amber-400">✔ Co-Pilots: Pierre & Leroy (Ready)</div>
                  <div className="text-blue-600 dark:text-blue-400">✔ Fuel Level: Sour Patch (94%)</div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
